import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { fetchReleases } from '../api/public'
import { CmsImage } from '../components/CmsImage'
import { useCrtPageTitle } from '../hooks/useCrtPageTitle'
import { usePageMeta } from '../hooks/usePageMeta'
import { schemaBreadcrumb, schemaGraph, schemaReleaseList, schemaWebPage } from '../lib/schema-org'

export function ReleasesPage() {
  useCrtPageTitle('Releases')
  const q = useQuery({
    queryKey: ['releases'],
    queryFn: fetchReleases,
  })

  const releases = q.data?.releases ?? []
  const jsonLd = useMemo(() => {
    const crumbs = schemaBreadcrumb(
      [
        { name: 'Home', path: '/' },
        { name: 'Releases', path: '/releases' },
      ],
      '/releases',
    )
    return schemaGraph(
      crumbs,
      schemaWebPage({
        path: '/releases',
        name: 'Releases — BNKR Records',
        description: 'Originals und Edits aus dem BNKR Records Katalog.',
        pageType: 'CollectionPage',
        breadcrumb: crumbs,
        mainEntity: schemaReleaseList(releases),
      }),
      schemaReleaseList(releases),
    )
  }, [releases])

  usePageMeta({
    title: 'Releases',
    description: 'BNKR Records — Releases, Originals und Edits.',
    canonical: '/releases',
    jsonLd,
  })

  return (
    <section className="section" style={{ paddingBottom: 'var(--sp-16)', paddingTop: 'var(--sp-4)' }}>
      <div className="wrap">
        <div className="sect-head">
          <div>
            <div className="kicker crt">↳ /releases</div>
            <h1 className="display h2" style={{ marginTop: 14 }}>
              Releases
            </h1>
          </div>
          <div className="lead" style={{ fontSize: 18, maxWidth: '50ch' }}>
            Originals und Edits aus dem BNKR Records Katalog.
          </div>
        </div>

        {q.isLoading && (
          <p className="muted" style={{ fontFamily: 'var(--mono)', letterSpacing: '.14em', fontSize: 13 }}>
            LOADING…
          </p>
        )}
        {q.isError && <p className="error">Releases konnten nicht geladen werden.</p>}
        {q.data && q.data.releases.length === 0 && (
          <p className="muted">Noch keine Releases.</p>
        )}
        <div className="release-grid">
          {q.data?.releases.map((r) => (
            <article key={r.id} className="release">
              <div className="art">
                {r.coverImageUrl ? (
                  <CmsImage
                    src={r.coverImageUrl}
                    size="md"
                    alt=""
                    width={640}
                    height={640}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : null}
              </div>
              <div className="body">
                <div className="meta">
                  {r.releaseType}
                  {r.releaseDate ? ` · ${new Date(r.releaseDate).toLocaleDateString('de-CH')}` : ''}
                </div>
                <h4>{r.title}</h4>
                <div className="links">
                  {r.spotifyUrl ? (
                    <a className="lnk" href={r.spotifyUrl} target="_blank" rel="noreferrer">
                      Spotify ↗
                    </a>
                  ) : null}
                  {r.soundcloudUrl ? (
                    <a className="lnk" href={r.soundcloudUrl} target="_blank" rel="noreferrer">
                      SoundCloud ↗
                    </a>
                  ) : null}
                  {r.mp3Url ? (
                    <a className="lnk" href={r.mp3Url} target="_blank" rel="noreferrer">
                      Play / DL ↗
                    </a>
                  ) : null}
                </div>
                {r.mp3Url ? (
                  <audio controls src={r.mp3Url} style={{ width: '100%', marginTop: 12 }}>
                    <track kind="captions" />
                  </audio>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
